import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Loader2, Users, MessageSquare, Paperclip, Mic } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { FileAttachment } from "@/components/chat/FileAttachment";
import { AttachmentDisplay } from "@/components/chat/AttachmentDisplay";
import { AudioRecorder } from "@/components/chat/AudioRecorder";

interface Client {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  unread_count?: number;
}

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  mime_type: string;
}

interface Message {
  id: string;
  client_id: string;
  sender_id: string;
  message: string;
  is_from_client: boolean;
  created_at: string;
  chat_attachments?: Attachment[];
}

export function ClientSupportChats() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchClientsWithChats();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchMessages(selectedClient.id);
      
      // Subscribe to realtime updates for this client
      const channel = supabase
        .channel(`client-chat-${selectedClient.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'client_support_messages',
            filter: `client_id=eq.${selectedClient.id}`
          },
          (payload) => {
            setMessages(prev => [...prev, payload.new as Message]);
            scrollToBottom();
            
            // Mark as read by agency
            markAsReadByAgency(payload.new.id);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [selectedClient]);

  const fetchClientsWithChats = async () => {
    try {
      // Get all clients who have sent messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('client_support_messages')
        .select('client_id')
        .order('created_at', { ascending: false });

      if (messagesError) throw messagesError;

      const uniqueClientIds = [...new Set(messagesData?.map(m => m.client_id))];

      if (uniqueClientIds.length === 0) {
        setLoading(false);
        return;
      }

      // Get client profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', uniqueClientIds);

      if (profilesError) throw profilesError;

      // Get unread counts for each client
      const clientsWithUnread = await Promise.all(
        (profilesData || []).map(async (profile) => {
          const { count } = await supabase
            .from('client_support_messages')
            .select('*', { count: 'exact', head: true })
            .eq('client_id', profile.id)
            .eq('read_by_agency', false)
            .eq('is_from_client', true);

          return {
            ...profile,
            unread_count: count || 0
          };
        })
      );

      setClients(clientsWithUnread);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (clientId: string) => {
    try {
      const { data: messagesData, error } = await supabase
        .from('client_support_messages')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch attachments separately
      if (messagesData && messagesData.length > 0) {
        const messageIds = messagesData.map(m => m.id);
        const { data: attachments } = await supabase
          .from('chat_attachments')
          .select('*')
          .in('message_id', messageIds);

        const messagesWithAttachments = messagesData.map(msg => ({
          ...msg,
          chat_attachments: attachments?.filter(att => att.message_id === msg.id) || []
        }));
        
        setMessages(messagesWithAttachments || []);
      } else {
        setMessages(messagesData || []);
      }
      
      // Mark all messages from this client as read
      await supabase
        .from('client_support_messages')
        .update({ read_by_agency: true })
        .eq('client_id', clientId)
        .eq('is_from_client', true);
      
      setTimeout(scrollToBottom, 100);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const markAsReadByAgency = async (messageId: string) => {
    await supabase
      .from('client_support_messages')
      .update({ read_by_agency: true })
      .eq('id', messageId);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const uploadAttachment = async (file: File, messageId: string) => {
    if (!user) return;

    const filePath = `${user.id}/${Date.now()}_${file.name}`;
    
    const { error: uploadError } = await supabase.storage
      .from('chat-attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { error: dbError } = await supabase
      .from('chat_attachments')
      .insert({
        message_id: messageId,
        file_name: file.name,
        file_path: filePath,
        file_type: file.type.split('/')[0],
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: user.id
      });

    if (dbError) throw dbError;
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && attachedFiles.length === 0) || !user || !selectedClient || sending) return;

    setSending(true);
    try {
      const { data: messageData, error: messageError } = await supabase
        .from('client_support_messages')
        .insert({
          client_id: selectedClient.id,
          sender_id: user.id,
          message: newMessage.trim() || '📎 Attachment',
          is_from_client: false,
          read_by_client: false
        })
        .select()
        .single();

      if (messageError) throw messageError;

      // Upload attachments
      for (const file of attachedFiles) {
        await uploadAttachment(file, messageData.id);
      }

      setNewMessage("");
      setAttachedFiles([]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValid = file.size <= 20 * 1024 * 1024; // 20MB
      if (!isValid) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 20MB limit`,
          variant: "destructive"
        });
      }
      return isValid;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAudioRecording = async (audioBlob: Blob) => {
    if (!user || !selectedClient || sending) return;

    setSending(true);
    try {
      const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
      
      const { data: messageData, error: messageError } = await supabase
        .from('client_support_messages')
        .insert({
          client_id: selectedClient.id,
          sender_id: user.id,
          message: '🎤 Voice message',
          is_from_client: false,
          read_by_client: false
        })
        .select()
        .single();

      if (messageError) throw messageError;

      await uploadAttachment(audioFile, messageData.id);
      setShowAudioRecorder(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 h-[calc(100vh-200px)] max-h-[800px]">
      {/* Clients List */}
      <Card className="glass-card lg:col-span-1 flex flex-col">
        <div className="p-4 sm:p-6 border-b border-border/50">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="text-base sm:text-lg font-semibold">Client Chats</h3>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">Select a client to view conversation</p>
        </div>

        <ScrollArea className="flex-1">
          {clients.length === 0 ? (
            <div className="p-4 sm:p-8 text-center text-muted-foreground">
              <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No client conversations yet</p>
            </div>
          ) : (
            <div className="p-2">
              {clients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className={`w-full p-3 sm:p-4 rounded-lg text-left transition-all touch-manipulation ${
                    selectedClient?.id === client.id
                      ? 'bg-primary/10 border-primary/50'
                      : 'hover:bg-muted/50'
                  } border border-transparent mb-2`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                        <AvatarFallback className="text-xs sm:text-sm">
                          {client.first_name?.[0]}{client.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm sm:text-base">
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{client.email}</p>
                      </div>
                    </div>
                    {(client.unread_count || 0) > 0 && (
                      <Badge variant="destructive" className="shrink-0 text-xs">
                        {client.unread_count}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className="glass-card lg:col-span-2 flex flex-col">
        {!selectedClient ? (
          <div className="flex-1 flex items-center justify-center p-4 sm:p-8 text-center text-muted-foreground">
            <div>
              <MessageSquare className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 opacity-50" />
              <p className="text-sm sm:text-base">Select a client to start chatting</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="p-4 sm:p-6 border-b border-border/50">
              <div className="flex items-center gap-2 sm:gap-3">
                <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                  <AvatarFallback className="text-xs sm:text-sm">
                    {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold truncate">
                    {selectedClient.first_name} {selectedClient.last_name}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">{selectedClient.email}</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4 sm:p-6" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-2 sm:gap-3 ${!msg.is_from_client ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.is_from_client && (
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                        <AvatarFallback className="text-xs sm:text-sm">
                          {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`flex flex-col max-w-[85%] sm:max-w-[70%] ${!msg.is_from_client ? 'items-end' : 'items-start'} gap-2`}>
                      <div
                        className={`rounded-2xl px-3 py-2 sm:px-4 sm:py-2.5 ${
                          !msg.is_from_client
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm sm:text-base break-words">{msg.message}</p>
                      </div>
                      
                      {msg.chat_attachments && msg.chat_attachments.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {msg.chat_attachments.map((attachment) => (
                            <AttachmentDisplay key={attachment.id} attachment={attachment} />
                          ))}
                        </div>
                      )}
                      
                      <span className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {!msg.is_from_client && (
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs sm:text-sm">You</AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-3 sm:p-4 border-t border-border/50 space-y-3">
              {/* Attached Files Preview */}
              {attachedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachedFiles.map((file, index) => (
                    <FileAttachment
                      key={index}
                      file={file}
                      onRemove={() => removeFile(index)}
                    />
                  ))}
                </div>
              )}

              {/* Audio Recorder */}
              {showAudioRecorder && (
                <AudioRecorder
                  onRecordingComplete={handleAudioRecording}
                  onCancel={() => setShowAudioRecorder(false)}
                />
              )}

              {/* Message Input */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending || attachedFiles.length >= 5}
                  variant="outline"
                  size="icon"
                  className="shrink-0 touch-manipulation h-10 w-10 sm:h-11 sm:w-11"
                >
                  <Paperclip className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>

                <Button
                  onClick={() => setShowAudioRecorder(!showAudioRecorder)}
                  disabled={sending}
                  variant="outline"
                  size="icon"
                  className="shrink-0 touch-manipulation h-10 w-10 sm:h-11 sm:w-11"
                >
                  <Mic className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
                
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                  placeholder="Type your message..."
                  disabled={sending}
                  className="flex-1 text-sm sm:text-base touch-manipulation"
                />
                
                <Button
                  onClick={sendMessage}
                  disabled={(!newMessage.trim() && attachedFiles.length === 0) || sending}
                  size="icon"
                  className="shrink-0 touch-manipulation h-10 w-10 sm:h-11 sm:w-11"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
